import React, { useState, Children } from 'react';
import { MapPin, Calendar, Users, ArrowRightLeft, Search } from 'lucide-react';
import { PassengerStepper } from './PassengerStepper';
import { toBengaliNumeral, formatDate } from '../utils/bengaliHelpers';
import { motion, AnimatePresence } from 'framer-motion';
interface SearchCardProps {
  onSearch: () => void;
}
export function SearchCard({
  onSearch
}: SearchCardProps) {
  const [showPassengers, setShowPassengers] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [adults, setAdults] = useState(1);
  const [children, setChildren] = useState(0);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = new Date(e.target.value);
    setSelectedDate(date);
    setShowDatePicker(false);
  };
  const formatDisplayDate = (date: Date) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (date.toDateString() === today.toDateString()) {
      return 'আজ';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'আগামীকাল';
    } else {
      return formatDate(date);
    }
  };
  return <div className="bg-white rounded-2xl shadow-lg p-5 space-y-5 border border-gray-100" data-id="element-77">
      {/* From/To Inputs */}
      <div className="space-y-4 relative" data-id="element-78">
        <div className="space-y-1" data-id="element-79">
          <label className="text-sm font-medium text-gray-500 font-bengali ml-1" data-id="element-80">
            কোথা থেকে
          </label>
          <div className="relative" data-id="element-81">
            <MapPin className="absolute left-3 top-3.5 text-[#006a4e]" size={20} data-id="element-82" />
            <input type="text" defaultValue="বাংলামটর" className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#006a4e] focus:border-[#006a4e] outline-none font-bengali text-lg font-medium" placeholder="বর্তমান অবস্থান" data-id="element-83" />
          </div>
        </div>

        <div className="absolute right-4 top-[40%] z-10 bg-white p-2 rounded-full shadow-md border border-gray-100 text-[#006a4e]" data-id="element-84">
          <ArrowRightLeft size={16} className="rotate-90" data-id="element-85" />
        </div>

        <div className="space-y-1" data-id="element-86">
          <label className="text-sm font-medium text-gray-500 font-bengali ml-1" data-id="element-87">
            কোথায় যাবেন
          </label>
          <div className="relative" data-id="element-88">
            <MapPin className="absolute left-3 top-3.5 text-green-600" size={20} data-id="element-89" />
            <input type="text" placeholder="গন্তব্য লিখুন" className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#006a4e] focus:border-[#006a4e] outline-none font-bengali text-lg font-medium" data-id="element-90" />
          </div>
        </div>
      </div>

      {/* Date & Passengers Grid */}
      <div className="grid grid-cols-2 gap-4" data-id="element-91">
        <div className="space-y-1" data-id="element-92">
          <label className="text-sm font-medium text-gray-500 font-bengali ml-1" data-id="element-93">
            কবে যাবেন
          </label>
          <div className="relative" data-id="element-94">
            <Calendar className="absolute left-3 top-3.5 text-gray-400 pointer-events-none z-10" size={18} data-id="element-95" />
            <button onClick={() => setShowDatePicker(!showDatePicker)} className="w-full pl-9 pr-2 py-3 bg-gray-50 border border-gray-200 rounded-xl font-bengali text-lg font-medium text-left" data-id="element-96">
              {formatDisplayDate(selectedDate)}
            </button>
            {showDatePicker && <AnimatePresence data-id="element-97">
                <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} className="absolute top-full mt-2 left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg p-3 z-20" data-id="element-98">
                  <input type="date" min={new Date().toISOString().split('T')[0]} value={selectedDate.toISOString().split('T')[0]} onChange={handleDateChange} className="w-full p-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#006a4e] focus:border-[#006a4e] outline-none" data-id="element-99" />
                </motion.div>
              </AnimatePresence>}
          </div>
        </div>

        <div className="space-y-1" data-id="element-100">
          <label className="text-sm font-medium text-gray-500 font-bengali ml-1" data-id="element-101">
            যাত্রী
          </label>
          <button onClick={() => setShowPassengers(!showPassengers)} className="w-full pl-9 pr-2 py-3 bg-gray-50 border border-gray-200 rounded-xl text-left relative" data-id="element-102">
            <Users className="absolute left-3 top-3.5 text-gray-400" size={18} data-id="element-103" />
            <span className="font-bengali text-lg font-medium" data-id="element-104">
              {toBengaliNumeral(adults + children)} জন
            </span>
          </button>
        </div>
      </div>

      {/* Passenger Dropdown */}
      {showPassengers && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-gray-50 p-4 rounded-xl border border-gray-200" data-id="element-105">
          <PassengerStepper label="প্রাপ্তবয়স্ক" subLabel="১২+ বছর" value={adults} onChange={setAdults} min={1} data-id="element-106" />
          <PassengerStepper label="শিশু" subLabel="২-১১ বছর" value={children} onChange={setChildren} data-id="element-107" />
        </motion.div>}

      {/* Search Button */}
      <button onClick={onSearch} className="w-full bg-[#006a4e] hover:bg-[#004d38] text-white py-4 rounded-xl font-bold text-xl font-bengali flex items-center justify-center space-x-2 shadow-lg shadow-green-200 active:scale-[0.98] transition-transform" data-id="element-108">
        <Search size={24} data-id="element-109" />
        <span data-id="element-110">রুট খুঁজুন</span>
      </button>
    </div>;
}